import { type ShippingLabelProvider } from '@hooks-api/types/dataModel/fpOrder'
import { Box } from '@pets-ui/Box/Box'
import { ButtonIcon } from '@pets-ui/Button/ButtonIcon'
import { HStack } from '@pets-ui/HStack/HStack'
import { OpenInNew } from '@pets-ui/Icons'
import { Link } from '@pets-ui/Link/Link'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { copyText } from '@utils'
import { useTranslation } from 'react-i18next'

const styles = stylex.create({
  openInNewLink: {
    alignItems: 'center',
    color: colors.grey800,
    display: 'flex',
    fontSize: 20,
  },
})

interface ShippingLabelProps {
  shippingLabel?: ShippingLabelProvider[]
}

const TRACKING_URL_WITH_QUERY_MAP: Record<string, string> = {
  順豐: 'https://htm.sf-express.com/tw/tc/dynamic_function/waybill/#search/bill-number/',
  黑貓: 'https://www.t-cat.com.tw/Inquire/TraceDetail.aspx?BillID=',
  宅配通大園: 'https://query2.e-can.com.tw/ECAN_APP/search.shtm',
}

export function ShippingLabel({ shippingLabel }: ShippingLabelProps) {
  const { t } = useTranslation()
  const hasShippingLabelData = shippingLabel && shippingLabel?.length > 0

  const groupedShippingLabels = shippingLabel?.reduce<ShippingLabelProvider[]>((acc, label) => {
    const existingProvider = acc.find(item => item.provider === label.provider)
    if (existingProvider) {
      existingProvider.shipping_labels.push(...label.shipping_labels)
    }
    else {
      acc.push({
        provider: label.provider,
        tracking_url: label.tracking_url,
        shipping_labels: [...label.shipping_labels],
      })
    }
    return acc
  }, [])

  const handleOpenLink = (trackingUrl?: string, trackingNo?: string) => {
    if (trackingNo) {
      copyText(trackingNo)
      window.alert(t('orderDetail.trackingAlert', { defaultValue: '已複製託運單號，將前往查詢托運狀態頁面' }))
    }
    window.open(trackingUrl, '_blank')
  }

  return (
    <Box mt={2} p={3} backgroundColor={colors.grey100} borderRadius={8}>
      {
        hasShippingLabelData
          ? (
            <VStack gap={3}>
              {
                groupedShippingLabels?.map((label) => {
                  return (
                    <HStack key={label.provider} justifyContent="space-between" alignItems="center">
                      <VStack gap={1} flex={1}>
                        <HStack>
                          <Text variant="body">
                            {t('orderDetail.trackingNo', { defaultValue: '託運單號：' })}
                            {label.provider || '-'}
                          </Text>
                        </HStack>
                        <HStack gap={2}>
                          {label.shipping_labels?.map((shippingLabel, index) => {
                            const query = TRACKING_URL_WITH_QUERY_MAP[label.provider] ? `${TRACKING_URL_WITH_QUERY_MAP[label.provider]}${shippingLabel.tracking_no}` : null
                            return (
                              <HStack gap={1} key={`${shippingLabel.tracking_no}-${index}`}>
                                <Text>{shippingLabel.tracking_no}</Text>
                                {query
                                  ? (
                                    <Link href={query} rel="noopener noreferrer" target="_blank" rootStyle={styles.openInNewLink}>
                                      <ButtonIcon>
                                        <OpenInNew fontSize={16} />
                                      </ButtonIcon>
                                    </Link>
                                  )
                                  : (
                                    <ButtonIcon onClick={() => handleOpenLink(label.tracking_url, shippingLabel.tracking_no)}>
                                      <OpenInNew fontSize={16} />
                                    </ButtonIcon>
                                  )}
                                ,
                              </HStack>
                            )
                          })}
                        </HStack>
                      </VStack>
                    </HStack>
                  )
                })
              }
            </VStack>
          )
          : (
            <HStack justifyContent="center" alignItems="center" height={68}>
              <Text color={colors.grey600}>{t('orderDetail.noTrackingNumber', { defaultValue: '尚無託運單號' })}</Text>
            </HStack>
          )
      }
    </Box>
  )
}
